import {
  MessagePayload,
  MessageResponse,
  MessageType,
  SendMessagePayload,
  SocketMessage,
} from "@/types/message";
import {
  PayloadAction,
  SerializedError,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { fetchMessages, sendUserMessage } from "@/services/conversationService";
import {
  addConversation,
  bumpConversation,
  markConversationUnread,
  setCurrConversation,
  keyVersionForAddress,
  resolveConversationAddressId,
} from "../control/controlSlice";
import { shallowEqual } from "react-redux";
import { setCacheEntry, invalidateCacheEntry, messageNeedsDecryptRetry } from "./conversationCacheSlice";

import { ChatState } from "@/types/chat";
import { RootState } from "../../redux/store";
import {
  CATALINA_ADDRESS_TYPE,
  EMAIL_ADDRESS_TYPE,
  FEDERATION_ADDRESS_TYPE,
  GROUP_ADDRESS_TYPE,
  KEYS_USERNAME,
  USER_ADDRESS_TYPE,
  USERID_ADDRESS_TYPE,
} from "@/constants/message";
import { convUserInfo } from "@/types/control";
import { formatFileToJson } from "@/utils/file";
import { emitMessageApi } from "@/services/api";
import { isJsonPayload, isValidJSON, sanitizeInput } from "@/utils/utils";
import { isEcdhWireFormat, isGroupMessageWireFormat } from "@/utils/crypto";
import { getCurrentFormattedDate } from "@/utils/time";
import type { GroupAddress } from "@/types/conversation";
import { resolveExternalDmAddressType } from "@/utils/addresses";
import {
  isGroupKeyDistributionMessage,
  isGroupKeyRequestMessage,
  answerPendingGroupKeyRequests,
  requestGroupKey,
  refreshConversationKey,
} from "@/services/groupKeyService";
import { isKeyExchangeMessage } from "@/services/keyExchangeService";

/** Normalize websocket/API DM address for comparison with conversation list entries. */
function normalizeDmAddress(address: string | GroupAddress): string {
  if (typeof address === "string") {
    return address;
  }
  return JSON.stringify(address);
}

// Every non-group type a DM thread can be filed under; GetConversations reports the external
// ones as with_type = ADDRESS_TYPE. Omitting one makes its inbound messages miss their own
// conversation and open a duplicate — and all three do receive. Catalina is two-way, and even
// email gets the router's delivery notices (EmailGateway::processMessage → queueErrorResponse).
const isUserDmAddressType = (addressType: number) =>
  addressType === USER_ADDRESS_TYPE ||
  addressType === USERID_ADDRESS_TYPE ||
  addressType === FEDERATION_ADDRESS_TYPE ||
  addressType === EMAIL_ADDRESS_TYPE ||
  addressType === CATALINA_ADDRESS_TYPE;

// Rebuild the list from `next` but keep the existing object reference for any unchanged
// message (matched by direction+msgId), so React doesn't remount unchanged rows and re-pull
// their attachments. Only changed rows (e.g. a 🔒 that just decrypted) get a new reference.
function mergePreservingIdentity(prev: MessageType[], next: MessageType[]): MessageType[] {
  const prevByKey = new Map(prev.map((m) => [`${m.direction}:${m.msgId}`, m]));
  return next.map((m) => {
    const existing = prevByKey.get(`${m.direction}:${m.msgId}`);
    return existing && shallowEqual(existing, m) ? existing : m;
  });
}

// Applies a freshly fetched message list to the live view, preserving object identity for
// unchanged rows (see mergePreservingIdentity). Scrolls to the bottom only when there's
// something new: forced on first populate, otherwise only if the newest message changed —
// so a background refresh never yanks a user reading history.
function applyFetchedMessages(
  state: ChatState,
  payload: { messages: MessageType[]; maxSmsgId: number; maxRmsgId: number }
) {
  const { messages, maxSmsgId, maxRmsgId } = payload;
  const wasEmpty = state.messages.length === 0;
  const prevLast = state.messages[state.messages.length - 1];
  const nextLast = messages[messages.length - 1];
  const tailChanged =
    !prevLast || !nextLast || prevLast.msgId !== nextLast.msgId || prevLast.direction !== nextLast.direction;

  state.messages = mergePreservingIdentity(state.messages, messages);
  state.maxSmsgId = maxSmsgId;
  state.maxRmsgId = maxRmsgId;
  if (messages.length > 0 && tailChanged) {
    const last = messages[messages.length - 1];
    state.selectedMessage = { id: last.msgId, direction: last.direction, force: wasEmpty, scrollToBottom: true };
  }
}

// This conversation's per-address key-material version (see keyVersionFor in controlSlice).
const keyVersionFor = (state: RootState, address: string): number =>
  keyVersionForAddress(state.control.keyExchangeVersions, address);

// Builds a setCacheEntry action snapshotting the live chat view for `address`.
const cacheEntryFromState = (address: string, state: RootState) =>
  setCacheEntry({
    address,
    messages: state.chat.messages,
    maxSmsgId: state.chat.maxSmsgId,
    maxRmsgId: state.chat.maxRmsgId,
    keyExchangeVersion: keyVersionFor(state, address),
  });

const initialState: ChatState = {
  loading: false,
  sending: false,
  messages: [],
  selectedMessage: null,
  maxSmsgId: 0,
  maxRmsgId: 0,
  reloadMessages: false,
  error: "",
};

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setReloadMessages: (state, action) => {
      state.reloadMessages = action.payload;
    },
    setMessages: (state, action) => {
      state.messages = action.payload;

      // If this is a new conversation, there's no message to scroll to
      if (action.payload.length === 0) {
        state.selectedMessage = null;
      } else {
        state.selectedMessage = {
          id: action.payload[action.payload.length - 1].msgId,
          direction: action.payload[action.payload.length - 1].direction,
          force: true,
          scrollToBottom: true,
        };
      }
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
      state.selectedMessage = {
        id: action.payload.msgId,
        direction: action.payload.direction,
        scrollToBottom: true,
        // Always jump to the bottom for our own sent message; an incoming message only
        // scrolls if the user is already at the bottom (handled by the scroll effect gate),
        // so it doesn't yank someone who's reading history.
        force: action.payload.direction === "sent",
      };

      if (action.payload.direction == "sent") {
        state.maxSmsgId += 1;
      } else {
        state.maxRmsgId = action.payload.msgId;
      }
    },
    setSelectedMessage: (state, action) => {
      state.selectedMessage = action.payload;
    },
    // Render a conversation instantly from the in-memory cache (already-decrypted rows).
    // Always preceded by setMessages([]) on the switch, so applyFetchedMessages' wasEmpty
    // branch yields the forced scroll-to-bottom.
    hydrateFromCache: (
      state,
      action: PayloadAction<{ messages: MessageType[]; maxSmsgId: number; maxRmsgId: number }>
    ) => {
      state.loading = false;
      applyFetchedMessages(state, action.payload);
    },
    // Full replace — unlike a dedup-append, picks up content changes (e.g. a 🔒 that decrypts).
    reconcileMessages: (
      state,
      action: PayloadAction<{ messages: MessageType[]; maxSmsgId: number; maxRmsgId: number }>
    ) => {
      applyFetchedMessages(state, action.payload);
    },
    // Append only-new messages from the scoped incremental fetch (deduped by direction+msgId).
    // Used when the cached view is fully decrypted, so we never re-decrypt existing rows.
    mergeIncrementalMessages: (
      state,
      action: PayloadAction<{ messages: MessageType[]; maxSmsgId: number; maxRmsgId: number }>
    ) => {
      // Advance the watermark even when nothing is new, so the next incremental starts fresh.
      state.maxSmsgId = Math.max(state.maxSmsgId, action.payload.maxSmsgId);
      state.maxRmsgId = Math.max(state.maxRmsgId, action.payload.maxRmsgId);
      const seen = new Set(state.messages.map((m) => `${m.direction}:${m.msgId}`));
      const toAdd = action.payload.messages.filter((m) => !seen.has(`${m.direction}:${m.msgId}`));
      if (toAdd.length === 0) return;
      // Insert before any trailing rows newer than the fetched batch (optimistic sends made
      // after hydration while this refresh was in flight) — a blind append would put the
      // older fetched rows below them and re-cache the misordered transcript.
      let insertAt = state.messages.length;
      const firstNewAt = new Date(toAdd[0].created).getTime();
      while (insertAt > 0 && new Date(state.messages[insertAt - 1].created).getTime() > firstNewAt) {
        insertAt--;
      }
      state.messages.splice(insertAt, 0, ...toAdd);
      const last = state.messages[state.messages.length - 1];
      state.selectedMessage = { id: last.msgId, direction: last.direction, scrollToBottom: true };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMessages.pending, (state: ChatState) => {
        state.loading = true;
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        state.loading = false;
        // A cache hit already hydrated the view (and kicked off a background refresh);
        // a stale result (user switched conversations mid-fetch) was cached in the thunk
        // and must not overwrite the newly shown conversation.
        if (action.payload.fromCache || action.payload.stale) return;
        // Key-exchange messages were already filtered out in the thunk.
        applyFetchedMessages(state, action.payload);
      })
      .addCase(loadMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load messages.";
      })
      // For sending
      .addCase(sendMessage.pending, (state) => {
        state.sending = true;
        state.error = "";
      })
      .addCase(sendMessage.fulfilled, (state: ChatState, action) => {
        state.sending = false;
        // User switched conversations mid-send — the live view isn't this conversation's.
        if (action.payload.stale) return;
        // Stamp the acked id onto the optimistic row, located by its id — a WS message
        // appended while the POST was in flight makes "last row" unreliable.
        for (let i = state.messages.length - 1; i >= 0; i--) {
          const m = state.messages[i];
          if (m.direction === "sent" && m.msgId === action.payload.optimisticMsgId) {
            m.msgId = action.payload.smsgid;
            break;
          }
        }
      })
      .addCase(sendMessage.rejected, (state: ChatState, action) => {
        state.sending = false;
        state.error = action.payload?.message || "Failed to send message";
        if (state.messages.length > 0) {
          state.messages[state.messages.length - 1].error = true;
        }
      })
      .addCase(resendMessage.fulfilled, (state, action) => {
        state.error = "";

        // update message w/no error + updated smsgid
        const target = state.messages.findIndex(
          (msg: MessageType) => {
            // only consider messages w/errors
            if (!msg.error) return false;

            // if media message, match by file name, otherwise match by message
            if (action.payload.input.media && isValidJSON(msg.message)) {
              const msgJson = JSON.parse(msg.message);
              return action.payload.input.media_filename == sanitizeInput(msgJson.fname);
            }

            return msg.message == action.payload.input.message
          }
        );
        if (target !== -1) {
          state.messages[target].error = false;
          state.messages[target].msgId = action.payload.smsgid;
          state.maxSmsgId = Math.max(state.maxSmsgId, action.payload.smsgid);
        }
      })
      // Clear messages on logout so a subsequent login doesn't show stale data.
      .addCase("auth/logout/fulfilled", (state) => {
        state.messages = [];
        state.selectedMessage = null;
        state.maxSmsgId = 0;
        state.maxRmsgId = 0;
        state.reloadMessages = false;
        state.error = "";
      });

  },
});

/**
 * Group-key follow-ups from a history fetch, fire-and-forget so they never block rendering:
 * as owner, answer requests that arrived while offline; as member, re-request keys proven stale.
 */
function handleHistoryKeyWork(
  response: Awaited<ReturnType<typeof fetchMessages>>,
  username: string | null | undefined,
  currentUserId: number | null | undefined
) {
  if (!username || !currentUserId) return;
  if (response.pendingKeyRequests.length > 0) {
    answerPendingGroupKeyRequests(response.pendingKeyRequests, username, currentUserId).catch(
      (err) => console.error("[GroupKey] Failed to answer pending key requests:", err)
    );
  }
  response.groupKeyRefreshes.forEach((address) => void requestGroupKey(address, username, currentUserId));
}

/**
 * Loads messages from a conversation by sending a req to backend
 */
export const loadMessages = createAsyncThunk(
  "chat/loadMessages",
  async (payload: convUserInfo, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const currentUserId = Number(state.auth.userId);
      const cacheKey = normalizeDmAddress(payload.address);

      // Plain conversation switch with a warm cache → render from memory now, refresh in the
      // background. Only the switch opts in via useCache; a key-arrival reload re-fetches
      // instead (re-decrypting rather than replaying stale cached ciphertext).
      const cached = state.conversationCache.entries[cacheKey];
      if (payload.useCache && cached && payload.maxSmsgId == null && payload.maxRmsgId == null) {
        dispatch(chatSlice.actions.hydrateFromCache(cached));
        dispatch(loadMessagesIncremental(payload));
        return { maxSmsgId: cached.maxSmsgId, maxRmsgId: cached.maxRmsgId, messages: cached.messages, fromCache: true, stale: false };
      }

      const params = {
        with_addr: payload.address,
        with_id: payload.addressId,
        with_type: payload.addressType,
        ...(payload.maxSmsgId != null && { max_smsg_id: payload.maxSmsgId }),
        ...(payload.maxRmsgId != null && { max_rmsg_id: payload.maxRmsgId }),
      };
      // Label history against a fresh copy of its key (first load per session; see refreshConversationKey).
      const { username, routerE2eeEnabled } = state.auth;
      if (username && currentUserId && routerE2eeEnabled) {
        await refreshConversationKey(payload.address, payload.addressType === GROUP_ADDRESS_TYPE, username, currentUserId);
      }

      const messageHistoryLimit = state.settings.messageHistoryLimit;
      const response = await fetchMessages(
        params.with_addr,
        params.with_id,
        params.with_type,
        currentUserId || undefined,
        params.max_smsg_id,
        params.max_rmsg_id,
        messageHistoryLimit
      );

      handleHistoryKeyWork(response, state.auth.username, currentUserId);

      // Cache the displayed (key-exchange-filtered) messages so a later switch renders instantly.
      const displayMessages = response.messages.filter((m) => !m.isKeyExchange);
      dispatch(setCacheEntry({
        address: cacheKey,
        messages: displayMessages,
        maxSmsgId: response.maxSmsgId,
        maxRmsgId: response.maxRmsgId,
        // Read fresh — a key may have arrived while the fetch was in flight.
        keyExchangeVersion: keyVersionFor(getState() as RootState, cacheKey),
      }));

      // If the user switched conversations mid-fetch, the entry above is still valid
      // (keyed to the fetched conversation) but the live view now belongs to another
      // conversation — mark the result stale so fulfilled doesn't overwrite it.
      const { conversations: convsNow, currConversationIndex: idxNow } = (getState() as RootState).control;
      const convNow = idxNow !== null ? convsNow[idxNow] : null;
      const stale = !convNow || normalizeDmAddress(convNow.address) !== cacheKey;

      return { ...response, messages: displayMessages, fromCache: false, stale };
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Background refresh after rendering a cached conversation: offset-fetch only messages newer
 * than the cached watermarks, or full re-fetch + reconcile when retryable 🔒 rows exist and
 * this conversation's key version moved. Best-effort — a failure leaves the cached view up.
 */
export const loadMessagesIncremental = createAsyncThunk(
  "chat/loadMessagesIncremental",
  async (payload: convUserInfo, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const currentUserId = Number(state.auth.userId) || undefined;
      const cacheKey = normalizeDmAddress(payload.address);
      const cached = state.conversationCache.entries[cacheKey];
      const messageHistoryLimit = state.settings.messageHistoryLimit;

      // Full re-fetch only when a retry can actually succeed (retryable 🔒 rows + this
      // conversation's key version moved); otherwise the same decrypts would fail identically.
      const retryUndecrypted =
        cached != null &&
        cached.hasUndecrypted &&
        cached.keyExchangeVersion !== keyVersionFor(state, cacheKey);
      const useOffset = cached != null && !retryUndecrypted;
      const response = await fetchMessages(
        payload.address,
        payload.addressId,
        payload.addressType,
        currentUserId,
        useOffset ? cached!.maxSmsgId : undefined,
        useOffset ? cached!.maxRmsgId : undefined,
        messageHistoryLimit
      );

      // Also here: cached DMs mostly refresh through this path, and the watermark advance
      // would otherwise skip their group_key_requests forever.
      handleHistoryKeyWork(response, state.auth.username, currentUserId);

      const fresh = response.messages.filter((m) => !m.isKeyExchange);

      // Bail if the user switched away mid-fetch — a later open re-fetches and self-corrects.
      const control = (getState() as RootState).control;
      const currConv =
        control.currConversationIndex !== null
          ? control.conversations[control.currConversationIndex]
          : null;
      if (!currConv || normalizeDmAddress(currConv.address) !== cacheKey) return;

      const payloadForReducer = { messages: fresh, maxSmsgId: response.maxSmsgId, maxRmsgId: response.maxRmsgId };
      if (useOffset) {
        dispatch(chatSlice.actions.mergeIncrementalMessages(payloadForReducer)); // append new
      } else {
        dispatch(chatSlice.actions.reconcileMessages(payloadForReducer)); // full replace (retries 🔒)
      }

      // Re-cache the resulting view (setCacheEntry recomputes hasUndecrypted and stamps
      // the current keyExchangeVersion, so the retry won't re-run until keys change again).
      dispatch(cacheEntryFromState(cacheKey, getState() as RootState));
    } catch (err) {
      console.error("[Cache] Background refresh failed:", err);
    }
  }
);

/**
 * Sends a message to a user/group, optimistically updating UI + persisting msg to backend.
 */
export const sendMessage = createAsyncThunk<
  // optimisticMsgId locates the optimistic row for the ack stamp (a WS message may have
  // been appended mid-POST, so "last row" is unreliable); stale means the user switched
  // conversations mid-send and the live view must not be touched.
  MessageResponse & { optimisticMsgId: number; stale: boolean },
  MessagePayload,
  {
    state: RootState;
    rejectValue: SerializedError;
  }
>(
  "chat/sendMessage",
  async (payload, { getState, dispatch, rejectWithValue }) => {
    let lat = 0;
    let lng = 0;
    if (getState().settings.locationEnabled) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            maximumAge: 30000,
          });
        });
        lat = position.coords.latitude;
        lng = position.coords.longitude;
      } catch {
        // Failed to get location — send without it
      }
    }

    const isEncryptedPayload = isEcdhWireFormat(payload.message) || isGroupMessageWireFormat(payload.message);
    const displayedMsg: MessageType = {
      addressId: payload.addressId,
      userId: Number(getState().auth.userId),
      created: new Date().toISOString(),
      direction: "sent",
      lat: lat ? String(lat) : "",
      lng: lng ? String(lng) : "",
      message: payload.displayMessage ?? payload.message,
      msgId: getState().chat.maxSmsgId + 1,
      isEncrypted: isEncryptedPayload,
    };
    const msgPayload: SendMessagePayload = {
      cmd: "SendUserMessage",
      lat,
      lng,
      media_filename: null,
      media_type: 0,
      media: null,
      message: payload.message,
      sendtime: new Date().getTime(),
      address_type: payload.addressType,
      address: payload.address,
      addressId: payload.addressId,
      userid: Number(getState().auth.userId),
      encrypted: isEncryptedPayload ? 1 : 0,
      e2e_keys_version: 0,
    };

    // Update the message payload if there's a file included & turn the file into a json str
    if (payload.file) {
      try {
        const fileInfo = await formatFileToJson(
          payload.file,
          payload.message // the file caption (optional)
        );

        msgPayload.media_filename = fileInfo.fname;
        msgPayload.media_type = fileInfo.dtype;
        msgPayload.media = fileInfo.media;

        displayedMsg.message = JSON.stringify(fileInfo);
      } catch (e) {
        return rejectWithValue(e as SerializedError);
      }
    }

    // Reorder conversation in list + add new message.
    // Use bumpConversation instead of moveCurrConversationToTop: the latter skips
    // the time update when the conversation is already at index 0.
    const { conversations, currConversationIndex } = getState().control;
    if (currConversationIndex !== null) {
      const currConv = conversations[currConversationIndex];
      dispatch(bumpConversation({ address: currConv.address, addressType: currConv.addressType }));
    }
    dispatch(addMessage(displayedMsg));

    // Persist msg to backend
    try {
      const response = await sendUserMessage(msgPayload);

      if (response.error) throw Error(response.error);

      // If the user switched conversations while the POST was in flight, the live view
      // (and any cache write built from it) belongs to another conversation — skip both
      // the cache update and the fulfilled stamp. The real sent row arrives via the
      // incremental fetch on the next open of this conversation.
      const sentConv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
      const { conversations: convsNow, currConversationIndex: idxNow } = getState().control;
      const convNow = idxNow !== null ? convsNow[idxNow] : null;
      const stale =
        !sentConv || !convNow ||
        normalizeDmAddress(convNow.address) !== normalizeDmAddress(sentConv.address);

      // An external thread has no sm_addr row — and so no address id — until this send created
      // one, and history queries filter on that id, not on with_addr. Pick it up now or the
      // thread reads as empty on its next open. Not gated on `stale`: the conversation needs
      // its id whether or not it's still on screen. Fire-and-forget; on failure the id arrives
      // with the next conversation list refresh.
      if (sentConv && !sentConv.addressId) {
        dispatch(resolveConversationAddressId(sentConv.address));
      }

      // Keep this conversation's cache in step with the just-sent message so switching
      // away and back doesn't momentarily drop it while the incremental fetch runs.
      // Stamp the acked server id onto the optimistic row (located by its id, not "last")
      // so the incremental fetch's dedup key matches the server's.
      if (!stale && sentConv) {
        const cacheKey = normalizeDmAddress(sentConv.address);
        if (getState().conversationCache.entries[cacheKey]) {
          const chat = getState().chat;
          dispatch(setCacheEntry({
            address: cacheKey,
            messages: chat.messages.map((m) =>
              m.direction === "sent" && m.msgId === displayedMsg.msgId ? { ...m, msgId: response.smsgid } : m
            ),
            maxSmsgId: Math.max(chat.maxSmsgId, response.smsgid),
            maxRmsgId: chat.maxRmsgId,
            keyExchangeVersion: keyVersionFor(getState(), cacheKey),
          }));
        }
      }

      // send message to websocket server to forward to other sessions if applicable
      // Non-critical: don't fail the send action if this sync call fails
      emitMessageApi.post(
        "/emit-message",
        {
          type: "syncSendMessage",
          payload: {
            excludedSessionId: localStorage.getItem("sessionId"),
            socketMsg: {
              address: payload.address,
              isGroup: isValidJSON(payload.address!),
              formattedMsg: {
                ...displayedMsg,
                msgId: response.smsgid,
              },
            },
          },
        }
      ).catch((e) => console.warn("[syncSendMessage] emit-message failed:", e));

      return { ...response, optimisticMsgId: displayedMsg.msgId, stale };
    } catch (e) {
      // Persist the failure into the cache (errored row included) so a switch-away and
      // back still shows the failed message with its resend affordance, instead of
      // hydrating the pre-send snapshot. Only while this conversation is still open —
      // after a switch the live view belongs to another conversation.
      const sentConv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
      const { conversations: convsNow, currConversationIndex: idxNow } = getState().control;
      const convNow = idxNow !== null ? convsNow[idxNow] : null;
      if (
        sentConv && convNow &&
        normalizeDmAddress(convNow.address) === normalizeDmAddress(sentConv.address)
      ) {
        const cacheKey = normalizeDmAddress(sentConv.address);
        if (getState().conversationCache.entries[cacheKey]) {
          const chat = getState().chat;
          dispatch(setCacheEntry({
            address: cacheKey,
            messages: chat.messages.map((m) =>
              m.direction === "sent" && m.msgId === displayedMsg.msgId ? { ...m, error: true } : m
            ),
            maxSmsgId: chat.maxSmsgId,
            maxRmsgId: chat.maxRmsgId,
            keyExchangeVersion: keyVersionFor(getState(), cacheKey),
          }));
        }
      }
      return rejectWithValue(e as SerializedError);
    }
  }
);

/**
 * Handles resend logic for failed sent messages
 */
export const resendMessage = createAsyncThunk<
  MessageResponse,
  MessagePayload,
  {
    state: RootState;
    rejectValue: SerializedError;
  }
>(
  "chat/resendMessage",
  async (payload, { getState, dispatch, rejectWithValue }) => {
    const isEncryptedResend = isEcdhWireFormat(payload.message) || isGroupMessageWireFormat(payload.message);
    const displayedMsg: MessageType = {
      addressId: payload.addressId,
      userId: Number(getState().auth.userId),
      created: new Date().toISOString(),
      direction: "sent",
      lat: "",
      lng: "",
      message: payload.message,
      msgId: getState().chat.maxSmsgId + 1,
      isEncrypted: isEncryptedResend,
    };
    const msgPayload: SendMessagePayload = {
      cmd: "SendUserMessage",
      lat: 0,
      lng: 0,
      media_filename: null,
      media_type: 0,
      media: null,
      message: payload.message,
      sendtime: new Date().getTime(),
      address_type: payload.addressType,
      address: payload.address,
      addressId: payload.addressId,
      userid: Number(getState().auth.userId),
      encrypted: isEncryptedResend ? 1 : 0,
      e2e_keys_version: 0,
    };

    // Update the message payload if there's a file included & turn the file into a json str.
    // Not isValidJSON: a failed message of just "42" parses, and reading .fname off the number
    // threw, so it could never be resent.
    if (isJsonPayload(payload.message)) {
      const mediaJSON = JSON.parse(payload.message);
      msgPayload.media_filename = mediaJSON.fname;
      msgPayload.media = mediaJSON.media;
      msgPayload.media_type = mediaJSON.fname.includes(".webp") ? 11 : 12;
      msgPayload.message = mediaJSON.msg;
    }

    // Persist msg to backend
    try {
      const response = await sendUserMessage(msgPayload);

      if (response.error) throw Error(response.error);

      // resendMessage.fulfilled fixes the errored row in the live view, but a cached copy
      // of this conversation may still hold it — drop the entry so the next open re-fetches.
      const { conversations, currConversationIndex } = getState().control;
      if (currConversationIndex !== null) {
        dispatch(invalidateCacheEntry(normalizeDmAddress(conversations[currConversationIndex].address)));
      }

      // send message to websocket server to forward to other sessions if applicable
      // Non-critical: don't fail the resend action if this sync call fails
      emitMessageApi.post(
        "/emit-message",
        {
          type: "syncSendMessage",
          payload: {
            excludedSessionId: localStorage.getItem("sessionId"),
            socketMsg: {
              address: payload.address,
              isGroup: isValidJSON(payload.address!),
              formattedMsg: {
                ...displayedMsg,
                msgId: response.smsgid,
              },
            },
          },
        }
      ).catch((e) => console.warn("[syncSendMessage] emit-message failed:", e));

      return response;
    } catch (e) {
      return rejectWithValue(e as SerializedError);
    }
  }
);

/**
 * Syncs an incoming chat message (sent or received) by:
 * - Finding the relevant conversation based on the message address
 * - Conditionally adding the message to the chat window if it's the active conversation
 * - Updating conversation ordering and timestamps
 *
 * Note that the payload's addressId is updated for sent messages only.
 */
export const syncMessage = createAsyncThunk<
  void,
  SocketMessage,
  { state: RootState }
>("chat/syncMessage", async (payload, { dispatch, getState }) => {
  const state = getState();
  const { conversations, currConversationIndex } = state.control;
  const incomingDmAddress = normalizeDmAddress(
    payload.address as string | GroupAddress
  );

  // Key exchange messages are handled transparently by processAndDispatch.
  // Never surface the @keys system user as a conversation in the UI.
  if (incomingDmAddress === KEYS_USERNAME) return;

  // Find the index of the conversation w/the new message
  const index = conversations.findIndex((conv) => {
    if (payload.isGroup && conv.addressType === GROUP_ADDRESS_TYPE) {
      const incomingGroup = isValidJSON(payload.address) ? JSON.parse(payload.address) : payload.address;
      const convGroup = JSON.parse(conv.address);
      return incomingGroup.gid == convGroup.gid;
    }
    return (
      !payload.isGroup &&
      isUserDmAddressType(conv.addressType) &&
      conv.address === incomingDmAddress
    );
  });

  // Update conversation time + reorder, but skip for key exchange protocol messages
  // (group_key_request, encrypted_group_key) — they're internal and should not
  // affect the conversation's last-active timestamp or sort order.
  // Sniff raw content only as a fallback (`??`, not `||`). When processing ran it set
  // isKeyExchange to a boolean and `message` is decrypted plaintext — sniffing that with
  // `||` would drop a user message that happens to be key-exchange JSON, and re-parse every
  // (multi-MB) message. Only the raw-fallback dispatch leaves isKeyExchange undefined.
  const rawMsg = payload.formattedMsg?.message ?? "";
  const isKeyExchange = payload.formattedMsg?.isKeyExchange
    ?? (isGroupKeyDistributionMessage(rawMsg)
      || isGroupKeyRequestMessage(rawMsg)
      || isKeyExchangeMessage(rawMsg));
  if (index !== -1 && !isKeyExchange) {
    const conv = conversations[index];
    dispatch(bumpConversation({ address: conv.address, addressType: conv.addressType }));
  }

  // Update messages if the conversation is open already
  if (index === currConversationIndex) {
    if (payload.formattedMsg.direction === "sent") {
      payload.formattedMsg.addressId = conversations[index].addressId;
    }
    // Key exchange protocol messages (group_key_request, encrypted_group_key, @keys
    // responses) are internal — never surface them in the conversation UI.
    if (isKeyExchange) return;
    // Deduplicate: a reload (triggered by key exchange) can race with the WS addMessage.
    // PHP stores the message before notifying the WS server, so the reload often fetches
    // the message from the DB before its WS event fires, causing the same message to be
    // added twice. Skip if a message with the same msgId+direction already exists.
    const { msgId, direction } = payload.formattedMsg;
    if (msgId && getState().chat.messages.some(
      (m) => m.msgId === msgId && m.direction === direction
    )) return;
    dispatch(addMessage(payload.formattedMsg));
    // Keep this conversation's cache current (if it's cached) so hydrate-on-return shows the
    // live message immediately rather than waiting for the next incremental fetch.
    const cacheKey = normalizeDmAddress(conversations[index].address);
    if (getState().conversationCache.entries[cacheKey]) {
      dispatch(cacheEntryFromState(cacheKey, getState()));
    }
  } else if (index === -1) {
    // Don't create a conversation for key exchange protocol messages.
    if (isKeyExchange) return;

    // An external sender's thread has to be filed under its own address type, or it misses the
    // row GetConversations returns on the next load and duplicates itself. The address doubles
    // as the display name: receiveMessage carries no fullname, and ConversationList matches
    // rows by it, so unnamed threads would collide on "New Conversation".
    const externalType = payload.isGroup
      ? null
      : resolveExternalDmAddressType(incomingDmAddress);

    dispatch(addConversation(
      {
        type: payload.isGroup ? "Group" : "User",
        fullname: payload.fullname ?? (externalType ? incomingDmAddress : "New Conversation"),
        address: incomingDmAddress,
        addressId: payload.formattedMsg.addressId,
        addressType: payload.isGroup
          ? GROUP_ADDRESS_TYPE
          : (externalType ?? USERID_ADDRESS_TYPE),
        groupName: payload.isGroup ? payload.fullname!.split(" ")[0] : "",
        updated: getCurrentFormattedDate()
      }
    ));
    if (currConversationIndex !== null) {
      dispatch(setCurrConversation(currConversationIndex + 1));
    }
  } else {
    // Message arrived for a non-current, existing conversation — mark it unread.
    // Don't mark as unread for key exchange protocol messages.
    if (isKeyExchange) return;

    // Re-find the index from current state (bumpConversation may have re-sorted the array).
    const updatedConvs = getState().control.conversations;
    const currentIndex = updatedConvs.findIndex((c) => {
      // Mirror the lookup that produced `index` above — matching USERNAME alone would fail to
      // re-find the row and silently skip the unread flag.
      const typeMatches = payload.isGroup
        ? c.addressType === GROUP_ADDRESS_TYPE
        : isUserDmAddressType(c.addressType);
      if (!typeMatches) return false;
      return c.address === conversations[index].address;
    });
    if (currentIndex !== -1) dispatch(markConversationUnread(currentIndex));
  }
});

// hydrateFromCache / reconcileMessages / mergeIncrementalMessages are internal to the
// cache flow — dispatched only via chatSlice.actions inside the thunks above.
export const {
  setReloadMessages,
  setMessages,
  addMessage,
  setSelectedMessage,
} = chatSlice.actions;

// Note that selectChatError isn't used for the sendMessage error toasts
export const selectChatError = (state: RootState) => state.chat.error;
export const selectMessagesLoading = (state: RootState) => state.chat.loading;
export const selectReloadMessages = (state: RootState) =>
  state.chat.reloadMessages;
export const selectMessages = (state: RootState) => state.chat.messages;
export const selectSelectedMessage = (state: RootState) =>
  state.chat.selectedMessage;
export const selectSending = (state: RootState) => state.chat.sending;
// True when the current view holds any retryable 🔒 row — a reload is only worthwhile then.
// Memoized: subscribers re-run selectors on every dispatch, and the O(n) scan should only
// pay when the message list itself changed.
export const selectHasUndecryptedMessages = createSelector(
  (state: RootState) => state.chat.messages,
  (messages) => messages.some(messageNeedsDecryptRetry)
);

export default chatSlice.reducer;
